import React from 'react';

import { ButtonProps as MuiButtonProps } from '@material-ui/core/Button';
import { makeStyles, Theme } from '@material-ui/core/styles';
import clsx from 'clsx';

import { Button } from '../../../../components/atoms';

type Border = 'both' | 'top' | 'bottom';

export interface SectionTextButtonProps
  extends Omit<MuiButtonProps, 'variant'> {
  border?: Border;
  className?: string;
  endIcon?: JSX.Element;
  size?: 'small' | 'medium' | 'large';
  startIcon?: JSX.Element;
  status?: 'default' | 'disabled';
  text?: string;
}

const useStyles = (props: SectionTextButtonProps) => {
  const { border = 'both' } = props;
  const hasBorderTop = border === 'both' || border === 'top';
  const hasBorderBottom = border === 'both' || border === 'bottom';

  return makeStyles((theme: Theme) => ({
    textButton: {
      borderBottom: hasBorderBottom
        ? `1px solid ${theme.palette.grey[400]}`
        : 'none',
      borderTop: hasBorderTop ? `1px solid ${theme.palette.grey[400]}` : 'none',
      cursor: 'default',
      padding: theme.spacing(1.5, 0),

      '& .MuiButton-label': {
        color: theme.palette.primary.main,
        fontWeight: theme.typography.fontWeightMedium,
        justifyContent: 'flex-start',

        '& .fd-text, & div, & span': {
          cursor: 'pointer',
        },
      },

      '&.MuiButton-sizeSmall': {
        padding: theme.spacing(1, 0),
      },

      '&.MuiButton-sizeLarge': {
        padding: theme.spacing(3, 0),
      },

      '&:hover': {
        borderBottom: hasBorderBottom
          ? `1px solid ${theme.palette.grey[400]}`
          : 'none',
      },
    },
  }))();
};

export function SectionTextButton(props: SectionTextButtonProps): JSX.Element {
  const classes = useStyles(props);
  const {
    className = '',
    endIcon = null,
    fullWidth = true,
    size = 'large',
    startIcon = null,
    status = 'default',
    text = '',
    ...rest
  } = props;

  const textButtonStyles = clsx(classes.textButton, className || '');

  return (
    <Button
      className={textButtonStyles}
      startIcon={startIcon}
      status={status}
      endIcon={endIcon}
      variant="text"
      size={size}
      fullWidth={fullWidth}
      {...rest}
    >
      {text}
    </Button>
  );
}
